import { referencePreviewSize } from "./referencePreviewSize";
import {
  useReferenceInteractions,
  referenceInteractionLog,
} from "../../CanvasReferencePicking";
import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNodeId } from "reactflow";
import { AudioReferenceThumbnail } from "./AudioReferenceLabelChip";
import { Play, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getAssetUrl,
  getLibraryThumbnailUrl,
} from "../../../../api/useAssetUrl";
import { useAuthenticatedMediaUrl } from "../../../../hooks/useAuthenticatedMediaUrl";
import { useBlockingOverlay } from "../../../../hooks/canvasShortcutGuard";
import { cn } from "../../../../lib/utils";
import { createNadDebug } from "../../../../utils/debugLog";
import { stripUrlQuery } from "../../../../utils/stripUrlQuery";
import type { SelectedReference } from "./ReferenceSelector";
import {
  VideoReferenceThumbnail,
  type VideoReferenceThumbnailSurface,
} from "./VideoReferenceThumbnail";

const referenceMediaPreviewLog = createNadDebug("dw:reference-media-preview");

export type ReferenceMediaPreviewSurface =
  | VideoReferenceThumbnailSurface
  | "video-editor-reference";

interface ReferenceMediaViewerProps {
  reference: SelectedReference | null;
  projectId: string | null | undefined;
  cacheBust?: string;
  ownerNodeId?: string;
  surface: ReferenceMediaPreviewSurface;
  onClose: () => void;
}

function resolveReferenceUrl(
  reference: SelectedReference,
  projectId: string | null | undefined,
  width: number,
  cacheBust?: string,
): string | null {
  if (reference.source === "library")
    return getLibraryThumbnailUrl(reference.assetPath);
  const isAbsolute =
    reference.assetPath.startsWith("http://") ||
    reference.assetPath.startsWith("https://");
  if (!projectId && !isAbsolute) return null;
  return getAssetUrl(projectId ?? "", reference.assetPath, width, cacheBust);
}

const ReferenceMediaViewer = memo(function ReferenceMediaViewer({
  reference,
  projectId,
  cacheBust,
  ownerNodeId,
  surface,
  onClose,
}: ReferenceMediaViewerProps) {
  const { t } = useTranslation("canvas");
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const fullUrl = reference
    ? resolveReferenceUrl(reference, projectId, 0, cacheBust)
    : null;
  const authenticatedUrl = useAuthenticatedMediaUrl(fullUrl);
  useBlockingOverlay(reference !== null && authenticatedUrl !== null);

  const close = useCallback(() => {
    if (reference) {
      referenceMediaPreviewLog({
        event: "reference-preview-close",
        trace_id: `reference-media-preview:${ownerNodeId ?? reference.nodeId}`,
        owner_node_id: ownerNodeId ?? null,
        reference_node_id: reference.nodeId,
        media_type: reference.mediaType,
        preview_surface: surface,
        source_path: stripUrlQuery(reference.assetPath),
      });
    }
    onClose();
  }, [onClose, ownerNodeId, reference, surface]);

  useEffect(() => {
    if (!reference || !authenticatedUrl) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.stopImmediatePropagation();
        close();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), video[controls], audio[controls], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !dialogRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !dialogRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.requestAnimationFrame(() => previouslyFocused?.focus());
    };
  }, [authenticatedUrl, close, reference]);

  if (!reference || !authenticatedUrl) return null;

  return createPortal(
    <div
      data-testid="reference-media-viewer"
      data-media-type={reference.mediaType}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-8 backdrop-blur-sm"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={reference.label}
        tabIndex={-1}
        className="relative flex max-h-full w-full max-w-6xl items-center justify-center"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          data-testid="reference-media-viewer-close"
          aria-label={t("imageHistoryPopover.close")}
          onClick={close}
          className="absolute -top-12 right-0 rounded-md text-text-secondary transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <X className="h-8 w-8" />
        </button>
        {reference.mediaType === "video" ? (
          <video
            src={authenticatedUrl}
            controls
            autoPlay
            playsInline
            tabIndex={0}
            aria-label={reference.label}
            className="max-h-[85vh] max-w-full rounded-lg bg-black shadow-2xl"
            data-testid="reference-media-viewer-video"
          />
        ) : reference.mediaType === "audio" ? (
          <audio
            src={authenticatedUrl}
            controls
            autoPlay
            tabIndex={0}
            aria-label={reference.label}
            className="w-full max-w-lg"
            data-testid="reference-media-viewer-audio"
          />
        ) : (
          <img
            src={authenticatedUrl}
            alt={reference.label}
            className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
            data-testid="reference-media-viewer-image"
          />
        )}
      </div>
    </div>,
    document.body,
  );
});

ReferenceMediaViewer.displayName = "ReferenceMediaViewer";

export interface ReferenceThumbnailButtonProps {
  reference: SelectedReference;
  projectId: string | null | undefined;
  cacheBust?: string;
  ownerNodeId?: string;
  surface: ReferenceMediaPreviewSurface;
  className?: string;
  mediaClassName?: string;
  testId?: string;
  /** Panel sizing and hover preview only; click gestures are shared everywhere. */
  panelInteractions?: boolean;
  audioTestId?: string;
}

const ReferenceThumbnailButton = memo(function ReferenceThumbnailButton({
  reference,
  projectId,
  cacheBust,
  ownerNodeId: explicitOwnerNodeId,
  surface,
  className,
  mediaClassName = "h-full w-full object-cover",
  testId,
  audioTestId,
  panelInteractions = false,
}: ReferenceThumbnailButtonProps) {
  const { t } = useTranslation("canvas");
  const [isOpen, setIsOpen] = useState(false);
  const interactions = useReferenceInteractions();
  const canvasNodeId = useNodeId();
  const ownerNodeId = explicitOwnerNodeId ?? canvasNodeId ?? undefined;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout>>();
  const [hovering, setHovering] = useState(false);
  const hintId = useId();
  const hintTimer = useRef<ReturnType<typeof setTimeout>>();
  const [hintPosition, setHintPosition] = useState<{ left: number; top: number } | null>(null);
  const hideHint = () => {
    clearTimeout(hintTimer.current);
    setHintPosition(null);
  };
  const scheduleHint = () => {
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => {
      const bounds = buttonRef.current?.getBoundingClientRect();
      if (!bounds) return;
      setHintPosition({
        left: Math.max(8, Math.min(bounds.left, window.innerWidth - 328)),
        top: bounds.bottom + 56 < window.innerHeight ? bounds.bottom + 8 : Math.max(8, bounds.top - 48),
      });
      referenceInteractionLog({
        event: "reference:instruction-visible",
        project_id: projectId,
        owner_node_id: ownerNodeId,
        reference_node_id: reference.nodeId,
        media_type: reference.mediaType,
        preview_surface: surface,
        delay_ms: 1000,
      });
    }, 1000);
  };
  const [loadedSize, setLoadedSize] = useState<{
    url: string | null;
    size: ReturnType<typeof referencePreviewSize>;
  } | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const fullUrl = useAuthenticatedMediaUrl(
    resolveReferenceUrl(reference, projectId, 0, cacheBust),
  );
  useEffect(() => () => {
    clearTimeout(clickTimer.current);
    clearTimeout(hintTimer.current);
  }, []);
  useEffect(() => {
    setDuration(null);
  }, [fullUrl]);
  const mediaSize = loadedSize?.url === fullUrl ? loadedSize.size : null;
  const reportPreview = (width: number, height: number) => {
    if (width <= 0 || height <= 0) return;
    const size = referencePreviewSize(width, height);
    setLoadedSize({ url: fullUrl, size });
    referenceInteractionLog({
      event: "reference:preview-ready",
      project_id: projectId,
      owner_node_id: ownerNodeId,
      reference_node_id: reference.nodeId,
      media_type: reference.mediaType,
      preview_width: size.width,
      preview_height: size.height,
      muted: reference.mediaType === "video",
    });
  };
  const rect = buttonRef.current?.getBoundingClientRect();
  const previewSize = mediaSize ?? { width: 200, height: 200 };
  const previewPosition = rect
    ? {
        left: Math.max(
          8,
          Math.min(
            rect.left + rect.width / 2 - previewSize.width / 2,
            window.innerWidth - previewSize.width - 8,
          ),
        ),
        top: Math.max(
          8,
          Math.min(
            rect.top - previewSize.height - 8,
            window.innerHeight - previewSize.height - 8,
          ),
        ),
      }
    : null;
  const previewUrl = resolveReferenceUrl(reference, projectId, 80, cacheBust);
  const canPreview =
    (reference.mediaType === "image" ||
      reference.mediaType === "video" ||
      reference.mediaType === "audio") &&
    resolveReferenceUrl(reference, projectId, 0, cacheBust) !== null;

  const open = () => {
    if (!canPreview) return;
    referenceMediaPreviewLog({
      event: "reference-preview-open",
      trace_id: `reference-media-preview:${ownerNodeId ?? reference.nodeId}`,
      owner_node_id: ownerNodeId ?? null,
      reference_node_id: reference.nodeId,
      media_type: reference.mediaType,
      preview_surface: surface,
      source_path: stripUrlQuery(reference.assetPath),
    });
    referenceInteractionLog({
      event: "reference:preview-open",
      project_id: projectId,
      owner_node_id: ownerNodeId,
      reference_node_id: reference.nodeId,
      media_type: reference.mediaType,
      preview_surface: surface,
    });
    setHovering(false);
    setIsOpen(true);
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        style={panelInteractions ? { width: 45, height: 45 } : undefined}
        onMouseEnter={() => {
          if (canPreview) scheduleHint();
          if (
            !panelInteractions ||
            !canPreview ||
            reference.mediaType === "audio"
          ) return;
          setHovering(true);
          referenceInteractionLog({
            event: "reference:hover-preview",
            feature: "canvas-reference-picking",
            project_id: projectId,
            owner_node_id: ownerNodeId,
            reference_node_id: reference.nodeId,
            media_type: reference.mediaType,
          });
        }}
        onMouseLeave={() => { setHovering(false); hideHint(); }}
        onFocus={() => { if (canPreview) scheduleHint(); }}
        onBlur={() => { setHovering(false); hideHint(); }}
        onKeyDown={(event) => { if (event.key === "Escape") hideHint(); }}
        onClick={(event) => {
          hideHint();
          event.stopPropagation();
          clearTimeout(clickTimer.current);
          if (event.detail === 0) {
            open();
          } else if (event.detail === 1) {
            // Keep the overlay from intercepting the second pointer click.
            clickTimer.current = setTimeout(open, 350);
          }
        }}
        onDoubleClick={(event) => {
          hideHint();
          event.stopPropagation();
          event.preventDefault();
          clearTimeout(clickTimer.current);
          setHovering(false);
          setIsOpen(false);
          referenceInteractionLog({
            event: "reference:focus-request",
            project_id: projectId,
            owner_node_id: ownerNodeId,
            reference_node_id: reference.nodeId,
            media_type: reference.mediaType,
            preview_surface: surface,
            preview_open: false,
          });
          if (ownerNodeId && reference.source !== "library")
            interactions?.focus(ownerNodeId, reference.nodeId);
        }}
        disabled={!canPreview}
        aria-describedby={hintPosition ? hintId : undefined}
        aria-label={`${t("designerNodeShell.expand")}: ${reference.label}`}
        data-testid={
          testId ??
          `reference-thumbnail-${reference.mediaType}-${reference.nodeId}`
        }
        data-reference-preview-trigger="true"
        data-reference-surface={surface}
        className={cn(
          "relative nodrag cursor-zoom-in overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-default",
          className,
        )}
      >
        {reference.mediaType === "video" ? (
          <VideoReferenceThumbnail
            projectId={projectId}
            nodeId={reference.nodeId}
            assetPath={reference.assetPath}
            alt={reference.label}
            className={mediaClassName}
            surface={surface}
            cacheBust={cacheBust}
            isLibrary={reference.source === "library"}
          />
        ) : reference.mediaType === "audio" ? (
          <AudioReferenceThumbnail
            label={reference.label}
            nativeTitle={false}
            testId={audioTestId}
            referenceNodeId={reference.nodeId}
            surface={surface}
            labelPlacement={panelInteractions ? "overlay" : "none"}
            className={
              panelInteractions
                ? "h-[45px] w-[45px] rounded-lg"
                : "h-full w-full rounded p-0.5"
            }
          />
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt={reference.label}
            className={mediaClassName}
            loading="lazy"
            onLoad={(event) => {
              if (!panelInteractions) return;
              // The thumbnail preserves the source ratio even when object-cover crops it.
              reportPreview(
                event.currentTarget.naturalWidth,
                event.currentTarget.naturalHeight,
              );
            }}
          />
        ) : (
          <span className="block h-full w-full bg-surface-2" />
        )}
        {panelInteractions && reference.mediaType === "video" && (
          <>
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Play
                data-testid="reference-video-play-icon"
                className="h-4 w-4 text-white fill-current drop-shadow"
              />
            </span>
            {duration !== null && (
              <span className="absolute bottom-0 right-0 rounded bg-background/80 px-1 text-xxs text-foreground">
                {duration.toFixed(1)}s
              </span>
            )}
          </>
        )}
      </button>
      {hintPosition && createPortal(
        <div
          id={hintId}
          role="tooltip"
          data-testid="reference-instruction-tooltip"
          className="fixed z-[9999] pointer-events-none w-max max-w-xs rounded border border-border bg-surface-2 px-2 py-1 text-xs text-foreground shadow-lg"
          style={hintPosition}
        >
          {t("referenceInteraction.previewHint")}
        </div>,
        document.body,
      )}
      {panelInteractions && reference.mediaType === "video" && fullUrl && (
        <video
          className="hidden"
          preload="metadata"
          muted
          src={fullUrl}
          onLoadedMetadata={(event) => {
            reportPreview(
              event.currentTarget.videoWidth,
              event.currentTarget.videoHeight,
            );
            if (Number.isFinite(event.currentTarget.duration))
              setDuration(event.currentTarget.duration);
          }}
        />
      )}
      {panelInteractions &&
        hovering &&
        fullUrl &&
        previewPosition &&
        createPortal(
          <div
            data-testid="reference-hover-preview"
            className="fixed z-[9998] pointer-events-none overflow-hidden rounded-lg border border-border bg-surface-0 shadow-xl"
            style={{
              ...previewPosition,
              ...previewSize,
              visibility: mediaSize ? "visible" : "hidden",
            }}
          >
            {reference.mediaType === "video" ? (
              <video
                src={fullUrl}
                muted
                autoPlay
                loop
                playsInline
                className="h-full w-full object-contain"
                onLoadedMetadata={(event) => {
                  reportPreview(
                    event.currentTarget.videoWidth,
                    event.currentTarget.videoHeight,
                  );
                }}
              />
            ) : (
              <img
                src={fullUrl}
                alt={reference.label}
                className="h-full w-full object-contain"
                onLoad={(event) => {
                  reportPreview(
                    event.currentTarget.naturalWidth,
                    event.currentTarget.naturalHeight,
                  );
                }}
              />
            )}
          </div>,
          document.body,
        )}
      <ReferenceMediaViewer
        reference={isOpen ? reference : null}
        projectId={projectId}
        cacheBust={cacheBust}
        ownerNodeId={ownerNodeId}
        surface={surface}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
});

ReferenceThumbnailButton.displayName = "ReferenceThumbnailButton";

export { ReferenceMediaViewer, ReferenceThumbnailButton, resolveReferenceUrl };
